import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  useTheme,
  useMediaQuery,
  alpha,
  Divider,
} from '@mui/material';
import { addMinutes, format, getHours, parse, subMinutes } from 'date-fns';
import { WbSunny as MorningIcon, Brightness5 as AfternoonIcon, Brightness3 as EveningIcon } from '@mui/icons-material';
import { useManualBooking } from '../../../context/ManualBookingContext';
import GradientButton from '../../../../components/buttons/GradientButton';
import TimeSlotGrid from './TimeSlotGrid';
import { slotFitsEmployeeSchedule } from '../../../../shared/booking/employee-schedule/workingSchedule';
import { resolveEffectiveCompanyHours } from '../../../../locations/resolveLocationHours';
import { parseBusinessTime } from '../../../../shared/booking/businessHours/parseBusinessTime';
import { listBlockingDayAppointments } from '../../../utils/dayAppointments';

const TimeSelectionSection = React.memo(({
  title,
  slots,
  icon,
  selectedTime,
  onTimeSelect,
  isMobile,
  isSelecting,
}) => {
  if (!slots.length) return null;

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
        {icon}
        <Typography variant="subtitle1" sx={{ ml: 1, fontWeight: 500 }}>
          {title}
        </Typography>
      </Box>
      <TimeSlotGrid
        timeSlots={slots}
        selectedTime={selectedTime}
        onTimeSelect={onTimeSelect}
        isMobile={isMobile}
        disabled={isSelecting}
      />
    </Box>
  );
});

const TimeStep = ({ onNext, onBack }) => {
  const { bookingData, updateBookingData } = useManualBooking();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const selectedDate = useMemo(
    () => (bookingData?.date ? new Date(bookingData.date) : null),
    [bookingData?.date]
  );
  const [selectedTime, setSelectedTime] = useState(bookingData?.time || null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const continueButtonRef = useRef(null);
  const appointmentsRef = useRef([]);
  const selectedAssignmentRef = useRef(null);

  const getTotalDuration = useCallback(() => {
    const serviceDuration = bookingData.selectedPackage?.duration
      ? parseInt(bookingData.selectedPackage.duration || 0, 10)
      : bookingData.selectedService?.duration || 60;
    const serviceBufferTime = bookingData.selectedPackage?.duration
      ? parseInt(bookingData.selectedPackage.bufferTime || 0, 10)
      : bookingData.selectedService?.bufferTime || 0;
    const addonsDuration = (bookingData.selectedAddons || []).reduce(
      (total, addon) => total + parseInt(addon.duration || 0, 10) + parseInt(addon.bufferTime || 0, 10),
      0
    );
    return serviceDuration + serviceBufferTime + addonsDuration;
  }, [bookingData.selectedAddons, bookingData.selectedPackage, bookingData.selectedService]);

  const getMaxConcurrentPerSlot = useCallback(() => {
    const raw = bookingData.companyData?.settings?.maxConcurrentAppointmentsPerSlot;
    const num = typeof raw === 'number' ? raw : raw != null ? parseInt(raw, 10) : NaN;
    return Number.isFinite(num) && num > 0 ? num : null;
  }, [bookingData.companyData]);

  const canEmployeeTakeSlot = useCallback((employee, slotStart, slotEnd, appointments) => {
    const companyHours = resolveEffectiveCompanyHours({
      location: bookingData.selectedLocation,
      companyData: bookingData.companyData,
      locationId: bookingData.locationId,
    });
    const allowDoubleBooking = Boolean(bookingData.companyData?.settings?.allowDoubleBooking);
    const maxConcurrentPerSlot = getMaxConcurrentPerSlot();

    const fitsSchedule = slotFitsEmployeeSchedule({
      workingSchedule: employee.workingSchedule,
      date: selectedDate,
      slotStart,
      slotEnd,
      companyHours,
    });

    if (!fitsSchedule) return false;

    const employeeAppointments = appointments.filter((appointment) => (
      !appointment.employeeId || appointment.employeeId === employee.id
    ));

    const overlappingCount = employeeAppointments.reduce((count, appointment) => {
      const overlaps =
        (slotStart >= appointment.startTime && slotStart < appointment.endTime) ||
        (slotEnd > appointment.startTime && slotEnd <= appointment.endTime) ||
        (slotStart < appointment.startTime && slotEnd > appointment.endTime);
      return overlaps ? count + 1 : count;
    }, 0);

    if (!allowDoubleBooking) {
      return overlappingCount === 0;
    }

    if (maxConcurrentPerSlot == null) {
      return true;
    }

    return overlappingCount < maxConcurrentPerSlot;
  }, [bookingData.companyData, bookingData.selectedLocation, bookingData.locationId, getMaxConcurrentPerSlot, selectedDate]);

  useEffect(() => {
    const fetchAvailableSlots = async () => {
      if (!selectedDate) {
        setError('Please select a date first.');
        setLoading(false);
        return;
      }

      if (!bookingData.selectedEmployee) {
        setError('Please select a staff member first.');
        setLoading(false);
        return;
      }

      if (!bookingData.selectedService) {
        setError('Please select a service first.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const dayKey = format(selectedDate, 'EEEE').toLowerCase();
        const companyHours = resolveEffectiveCompanyHours({
          location: bookingData.selectedLocation,
          companyData: bookingData.companyData,
          locationId: bookingData.locationId,
        });
        const companyDay = companyHours?.[dayKey];

        if (!companyDay?.isOpen) {
          setAvailableSlots([]);
          setError('The business is closed on this date.');
          return;
        }

        const openTime = parseBusinessTime(companyDay.open, selectedDate);
        const closeTime = parseBusinessTime(companyDay.close, selectedDate);

        if (!openTime || !closeTime || !(closeTime > openTime)) {
          setAvailableSlots([]);
          setError('Business hours are not configured correctly for this location.');
          return;
        }
        const totalDuration = getTotalDuration();
        const lastSlot = subMinutes(closeTime, totalDuration);

        const appointments = await listBlockingDayAppointments({
          companyId: bookingData.companyData.id,
          selectedDate,
          fallbackDuration: totalDuration,
        });
        appointmentsRef.current = appointments;

        const slots = [];
        let cursor = new Date(selectedDate);
        cursor.setHours(openTime.getHours(), openTime.getMinutes(), 0, 0);

        while (cursor <= lastSlot) {
          const slotStart = new Date(cursor);
          const slotEnd = addMinutes(slotStart, totalDuration);

          if (slotStart > new Date()) {
            if (bookingData.selectedEmployee?.isAnyAvailable) {
              const matchingEmployee = bookingData.selectedEmployee.availableEmployees?.find((employee) => (
                canEmployeeTakeSlot(employee, slotStart, slotEnd, appointments)
              ));

              if (matchingEmployee) {
                slots.push(slotStart);
              }
            } else if (canEmployeeTakeSlot(bookingData.selectedEmployee, slotStart, slotEnd, appointments)) {
              slots.push(slotStart);
            }
          }

          cursor = addMinutes(cursor, 15);
        }

        setAvailableSlots(slots);
      } catch (fetchError) {
        console.error('Error loading time slots:', fetchError);
        setAvailableSlots([]);
        setError(fetchError.message || 'Failed to load time slots.');
      } finally {
        setLoading(false);
      }
    };

    fetchAvailableSlots();
  }, [
    bookingData.companyData,
    bookingData.selectedEmployee,
    bookingData.selectedService,
    bookingData.selectedLocation,
    bookingData.locationId,
    canEmployeeTakeSlot,
    getTotalDuration,
    selectedDate,
  ]);

  const { morningSlots, afternoonSlots, eveningSlots } = useMemo(() => ({
    morningSlots: availableSlots.filter((slot) => getHours(slot) >= 0 && getHours(slot) < 12),
    afternoonSlots: availableSlots.filter((slot) => getHours(slot) >= 12 && getHours(slot) < 17),
    eveningSlots: availableSlots.filter((slot) => getHours(slot) >= 17),
  }), [availableSlots]);

  const handleTimeSelect = useCallback((time) => {
    setIsSelecting(true);
    setSelectedTime(time);

    if (bookingData.selectedEmployee?.isAnyAvailable) {
      const slotEnd = addMinutes(time, getTotalDuration());
      const employee = bookingData.selectedEmployee.availableEmployees?.find((candidate) => (
        canEmployeeTakeSlot(candidate, time, slotEnd, appointmentsRef.current)
      ));

      selectedAssignmentRef.current = employee
        ? {
            employeeId: employee.id,
            employeeName: employee.name,
            employee,
          }
        : null;
    } else {
      selectedAssignmentRef.current = null;
    }

    requestAnimationFrame(() => {
      continueButtonRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
      setIsSelecting(false);
    });
  }, [bookingData.companyData, bookingData.selectedEmployee, bookingData.selectedLocation, bookingData.locationId, getTotalDuration, selectedDate]);

  const handleContinue = () => {
    if (!selectedDate || !selectedTime) {
      setError('Please select a time before continuing.');
      return;
    }

    const updateData = {
      date: selectedDate,
      time: selectedTime,
      selectedDateTime: selectedTime,
    };

    if (bookingData.selectedEmployee?.isAnyAvailable && selectedAssignmentRef.current) {
      updateData.selectedEmployee = {
        ...bookingData.selectedEmployee,
        employeeId: selectedAssignmentRef.current.employeeId,
        actualEmployeeName: selectedAssignmentRef.current.employeeName,
        actualEmployee: selectedAssignmentRef.current.employee,
      };
      updateData.employeeId = selectedAssignmentRef.current.employeeId;
      updateData.employeeName = selectedAssignmentRef.current.employeeName;
    }

    updateBookingData(updateData);
    onNext();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight="600">
          {selectedDate ? format(selectedDate, 'EEEE, MMMM d') : 'Choose a time'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Available times update based on the selected staff member and their working schedule.
        </Typography>
      </Box>

      <Box sx={{ p: 3, borderRadius: 2 }}>
        {error ? (
          <Box sx={{ p: 2, bgcolor: alpha(theme.palette.error.main, 0.1), borderRadius: 1 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : availableSlots.length === 0 ? (
          <Box sx={{ p: 2, bgcolor: alpha(theme.palette.warning.main, 0.1), borderRadius: 1 }}>
            <Typography color="text.secondary">
              No available time slots for this date. Please go back and choose another date or staff member.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ minHeight: 200 }}>
            <TimeSelectionSection
              title="Morning"
              slots={morningSlots}
              icon={<MorningIcon sx={{ color: theme.palette.warning.light }} />}
              selectedTime={selectedTime}
              onTimeSelect={handleTimeSelect}
              isMobile={isMobile}
              isSelecting={isSelecting}
            />

            {morningSlots.length > 0 && afternoonSlots.length > 0 && <Divider sx={{ my: 2 }} />}

            <TimeSelectionSection
              title="Afternoon"
              slots={afternoonSlots}
              icon={<AfternoonIcon sx={{ color: theme.palette.warning.main }} />}
              selectedTime={selectedTime}
              onTimeSelect={handleTimeSelect}
              isMobile={isMobile}
              isSelecting={isSelecting}
            />

            {afternoonSlots.length > 0 && eveningSlots.length > 0 && <Divider sx={{ my: 2 }} />}

            <TimeSelectionSection
              title="Evening"
              slots={eveningSlots}
              icon={<EveningIcon sx={{ color: theme.palette.info.dark }} />}
              selectedTime={selectedTime}
              onTimeSelect={handleTimeSelect}
              isMobile={isMobile}
              isSelecting={isSelecting}
            />
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 2,
          mt: 4,
          pt: 3,
          borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
        <GradientButton onClick={onBack} variant="primary">
          Back
        </GradientButton>
        <GradientButton
          ref={continueButtonRef}
          variant="success"
          onClick={handleContinue}
          disabled={!selectedTime}
        >
          Continue
        </GradientButton>
      </Box>
    </Box>
  );
};

export default React.memo(TimeStep);
